import { esc, showErr } from './users-shared';
import { t } from '../ui/lang';
/**
 * Source-IP whitelist editor for a team's integration API key, rendered inside the
 * Users → API tab right under the key itself (see team-api-tab.ts).
 *
 * The list is the second lock on the same endpoint: the key says WHO is calling, this
 * says FROM WHERE. Empty means no IP restriction — spelled out in the UI, because the
 * other whitelist in the panel (remote ADB) reads an empty list as "deny all" and the
 * difference must not be guessed.
 *
 * Backed by GET /api/teams/:id/api-key (which returns `ips` alongside the key) and
 * PUT /api/teams/:id/api-ips. Validation here is only fast feedback — the server
 * re-validates every entry and rejects the whole request on the first bad one.
 */
/** Sanity check for an IP or CIDR block. Deliberately loose: the server's net.isIP is
 *  the authority, this only catches obvious typos before a round trip. */
function looksLikeRule(value) {
    const [addr, prefix, ...rest] = value.split('/');
    if (rest.length > 0 || !addr)
        return false;
    const isV4 = /^(\d{1,3})(\.\d{1,3}){3}$/.test(addr) && addr.split('.').every((o) => Number(o) <= 255);
    const isV6 = addr.includes(':') && /^[0-9a-fA-F:.]+$/.test(addr);
    if (!isV4 && !isV6)
        return false;
    if (prefix === undefined)
        return true;
    return /^\d{1,3}$/.test(prefix) && Number(prefix) <= (isV4 ? 32 : 128);
}
export function renderApiIpSection(container, team, initial) {
    let ips = [...initial];
    container.innerHTML = `
        <div class="settings-section">
            <h3 class="settings-section__title">${t('users.allowed_ips')}</h3>
            <p class="settings-hint">
                ${t('users.allowed_ips_hint')}
            </p>
            <div class="modal__field" style="max-width:420px">
                <label for="tai-input">${t('users.add_ip_label')}</label>
                <div style="display:flex;gap:8px">
                    <input class="settings-input" id="tai-input" type="text" autocomplete="off" placeholder="${t('users.ip_placeholder')}" />
                    <button type="button" class="modal__submit" id="tai-add">${t('common.add')}</button>
                </div>
            </div>
            <div id="tai-list" style="margin:12px 0;max-width:420px"></div>
            <div class="settings-error modal__error" id="tai-error" style="display:none"></div>
            <div class="users-toolbar">
                <button type="button" class="tbl-btn" id="tai-save">${t('common.save')}</button>
                <span class="settings-hint" id="tai-status"></span>
            </div>
        </div>
    `;
    const input = container.querySelector('#tai-input');
    const listEl = container.querySelector('#tai-list');
    const errorEl = container.querySelector('#tai-error');
    const statusEl = container.querySelector('#tai-status');
    const saveBtn = container.querySelector('#tai-save');
    function renderList() {
        listEl.innerHTML = ips.length === 0
            ? `<p class="settings-hint">${t('users.ips_empty')}</p>`
            : ips.map((ip, i) => `
                <div class="clicker-farm-row" style="cursor:default;padding:6px 10px">
                    <span style="flex:1"><code>${esc(ip)}</code></span>
                    <button type="button" class="tbl-btn tai-del" data-i="${i}" title="${t('common.remove')}">✕</button>
                </div>
            `).join('');
        listEl.querySelectorAll('.tai-del').forEach((b) => {
            b.addEventListener('click', () => {
                ips.splice(parseInt(b.dataset['i'] ?? '', 10), 1);
                renderList();
            });
        });
    }
    /** Add whatever is typed. Returns false (with the error shown) on invalid input, so
     *  Save can reuse it for the "typed but not added" entry instead of losing it. */
    function addPending() {
        errorEl.style.display = 'none';
        statusEl.textContent = '';
        const value = input.value.trim().toLowerCase();
        if (!value)
            return true;
        if (!looksLikeRule(value)) {
            showErr(errorEl, t('users.invalid_ip', { value }));
            return false;
        }
        if (!ips.includes(value))
            ips.push(value);
        input.value = '';
        renderList();
        return true;
    }
    container.querySelector('#tai-add').addEventListener('click', () => { addPending(); input.focus(); });
    input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addPending();
        }
    });
    saveBtn.addEventListener('click', async () => {
        if (!addPending())
            return;
        saveBtn.disabled = true;
        try {
            const res = await fetch(`/api/teams/${team.id}/api-ips`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ips }),
            });
            if (!res.ok) {
                showErr(errorEl, (await res.json().catch(() => ({}))).error ?? t('common.failed_to_save'));
                return;
            }
            // The server normalizes and de-dupes, so redraw from what it stored rather
            // than from what was typed.
            ips = (await res.json()).ips ?? ips;
            renderList();
            statusEl.textContent = ips.length === 0 ? t('users.saved_no_ip_restriction') : t('common.saved');
        }
        catch {
            showErr(errorEl, t('users.network_error'));
        }
        finally {
            saveBtn.disabled = false;
        }
    });
    renderList();
}
